/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { setGlobalMessageFromException } from "sh-application";
import { BACKGROUND_DB_DATA_REFERSH_DELAY } from "sh-application/../StaffHubConstants";
import { default as AppDbFactory } from "sh-services/data/AppDb";
import { ITeamDatabase } from "sh-services/data/ITeamDatabase";
import { ITenantDatabase } from "sh-services/data/ITenantDatabase";
import { IUserDatabase } from "sh-services/data/IUserDatabase";
import { IDataProvider } from "sh-services/dataproviders/IDataProvider";
import { ECSConfigService, ECSConfigKey } from "sh-services";

import { ICrossLocationOpenShiftDatabase } from "../data/ICrossLocationOpenShiftDatabase";
import { ICrossLocationTeamDatabase } from "../data/ICrossLocationTeamDatabase";
import { INoteDatabase } from "../data/INoteDatabase";
import { IOpenShiftDatabase } from "../data/IOpenShiftDatabase";
import { IShiftDatabase } from "../data/IShiftDatabase";
import { IUserProfileDatabase } from "../data/IUserProfileDatabase";
import { ScheduleFiltersDatabase } from "../data/ScheduleFiltersDatabase";

import IDataService from "./IDataService";
import { ICrossLocationShiftDatabase } from "../data/ICrossLocationShiftDatabase";

export declare type IsCanceledDelegate = () => boolean;

/**
 * Base DataService provides a mechanism for the data services to share common logic.
 * E.g. accessing the databases and logged in user state
 */
export abstract class DataService implements IDataService {

    protected loggedInUserId: string;
    protected tenantId: string;
    protected sessionId: string;
    protected teamDatabase: ITeamDatabase;
    protected userDatabase: IUserDatabase;
    protected tenantDatabase: ITenantDatabase;
    protected shiftDatabase: IShiftDatabase;
    protected myShiftDatabase: IShiftDatabase;
    protected noteDatabase: INoteDatabase;
    protected openShiftDatabase: IOpenShiftDatabase;
    protected scheduleFiltersDatabase: ScheduleFiltersDatabase;
    protected crossLocationOpenShiftDatabase: ICrossLocationOpenShiftDatabase;
    protected crossLocationShiftDatabase: ICrossLocationShiftDatabase;
    protected crossLocationTeamDatabase: ICrossLocationTeamDatabase;
    protected userProfileDatabase: IUserProfileDatabase;

    /**
     * Set the user as logged in
     * @param loggedInUserId Logged in user Id
     */
    public async setUserLoggedIn(loggedInUserId: string, tenantId: string, sessionId: string): Promise<void> {
        this.loggedInUserId = loggedInUserId;
        this.tenantId = tenantId;
        this.sessionId = sessionId;
        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.ParellelizeIndexedDbInitialization)) {
            [
                this.teamDatabase,
                this.userDatabase,
                this.tenantDatabase,
                this.shiftDatabase,
                this.myShiftDatabase,
                this.noteDatabase,
                this.openShiftDatabase,
                this.scheduleFiltersDatabase,
                this.crossLocationOpenShiftDatabase,
                this.crossLocationShiftDatabase,
                this.crossLocationTeamDatabase,
                this.userProfileDatabase
            ] = await Promise.all([
                AppDbFactory.loadTeamDatabase(loggedInUserId),
                AppDbFactory.loadUserDatabase(loggedInUserId),
                AppDbFactory.loadTenantDatabase(loggedInUserId),
                AppDbFactory.loadShiftsDatabase(loggedInUserId),
                AppDbFactory.loadMyShiftsDatabase(loggedInUserId),
                AppDbFactory.loadNotesDatabase(loggedInUserId),
                AppDbFactory.loadOpenShiftsDatabase(loggedInUserId),
                AppDbFactory.loadScheduleFiltersDatabase(loggedInUserId),
                AppDbFactory.loadCrossLocationOpenShiftDatabase(loggedInUserId),
                AppDbFactory.loadCrossLocationShiftDatabase(loggedInUserId),
                AppDbFactory.loadCrossLocationTeamDatabase(loggedInUserId),
                AppDbFactory.loadUserProfileDatabase(loggedInUserId)
            ]);
        } else {
            this.teamDatabase = await AppDbFactory.loadTeamDatabase(loggedInUserId);
            this.userDatabase = await AppDbFactory.loadUserDatabase(loggedInUserId);
            this.tenantDatabase = await AppDbFactory.loadTenantDatabase(loggedInUserId);
            this.shiftDatabase = await AppDbFactory.loadShiftsDatabase(loggedInUserId);
            this.myShiftDatabase = await AppDbFactory.loadMyShiftsDatabase(loggedInUserId);
            this.noteDatabase = await AppDbFactory.loadNotesDatabase(loggedInUserId);
            this.openShiftDatabase = await AppDbFactory.loadOpenShiftsDatabase(loggedInUserId);
            this.scheduleFiltersDatabase = await AppDbFactory.loadScheduleFiltersDatabase(loggedInUserId);
            this.crossLocationOpenShiftDatabase = await AppDbFactory.loadCrossLocationOpenShiftDatabase(loggedInUserId);
            this.crossLocationShiftDatabase = await AppDbFactory.loadCrossLocationShiftDatabase(loggedInUserId);
            this.crossLocationTeamDatabase = await AppDbFactory.loadCrossLocationTeamDatabase(loggedInUserId);
            this.userProfileDatabase = await AppDbFactory.loadUserProfileDatabase(loggedInUserId);
        }
    }

    /**
     * Set the user as logged out
     */
    public async setUserLoggedOut(): Promise<void> {
        if (this.loggedInUserId) {
            // Clear the logged in user's state and persistent cache
            await AppDbFactory.deleteAppDb(this.loggedInUserId);
            this.loggedInUserId = null;
            this.teamDatabase = null;
            this.userDatabase = null;
            this.tenantDatabase = null;
            this.shiftDatabase = null;
            this.myShiftDatabase = null;
            this.noteDatabase = null;
            this.openShiftDatabase = null;
            this.scheduleFiltersDatabase = null;
            this.crossLocationOpenShiftDatabase = null;
            this.crossLocationShiftDatabase = null;
            this.crossLocationTeamDatabase = null;
            this.userProfileDatabase = null;
        }
    }

    /**
     * Returns the tenant id
     */
    public getTenantId(): string {
        return this.tenantId;
    }

    /**
     * Fetches data based on our priority sequence:
     *      1. Fetch Data from Memory
     *      2. Fetch Data from Database
     *      3. Fetch Data from Network
     * If the data is not in the Memory, it triggers an async process to also update the DB and memory from the network
     * @param dataProvider Data provider
     * @param forceLoadFromNetwork Forces data to load from the network even if it's already in cache
     */
    protected async getData<T>(
        dataProvider: IDataProvider<T>,
        forceLoadFromNetwork: boolean = false
    ): Promise<T> {
        //
        if (forceLoadFromNetwork) {
            return await this.loadDataFromNetwork(dataProvider);
        }

        // Check if the data is in cache, other load it from the network
        const dataFromCache: T = await this.loadDataFromCache(dataProvider);
        if (dataFromCache != undefined && dataFromCache != null) {
            return dataFromCache;
        } else {
            return await this.loadDataFromNetwork(dataProvider);
        }
    }

    /**
     * Gets the data from memory (otherwise tries to fetch it from the database and load it into memory)
     * @param dataProvider Data provider
     * @param skipRefreshFromNetworkIfNotInMemory (optional) - Skip refreshing data from network if it was only in the database and not in memory
     */
    public async loadDataFromCache<T>(dataProvider: IDataProvider<T>, skipRefreshFromNetworkIfNotInMemory?: boolean): Promise<T> {
        // Check if the data is already loaded in memory
        const dataFromMemory: T = await dataProvider.getDataFromMemory();
        if (dataFromMemory !== undefined && dataFromMemory !== null) {
            return dataFromMemory;
        } else {
            let dataFromDatabaseOrDefault: T = await dataProvider.getDataFromDatabase();

            // if the data is neither in memory nor the database, load default data
            if (dataFromDatabaseOrDefault == undefined || dataFromDatabaseOrDefault == null) {
                dataFromDatabaseOrDefault = await dataProvider.getDefaultData();
            }

            if (dataFromDatabaseOrDefault != undefined && dataFromDatabaseOrDefault != null) {
                // load the data into the global store
                await dataProvider.setDataInMemory(dataFromDatabaseOrDefault);

                // if we found the data in the database, check if we want to refresh the data from the network
                if (skipRefreshFromNetworkIfNotInMemory === undefined) {
                    skipRefreshFromNetworkIfNotInMemory = await dataProvider.skipRefreshFromNetworkIfNotInMemory(dataFromDatabaseOrDefault);
                }

                if (!skipRefreshFromNetworkIfNotInMemory) {
                    // trigger an update from the network, but don't await. We delay this so that other requests get priority
                    setTimeout(async () => {
                        try {
                            await this.loadDataFromNetwork(dataProvider);
                        } catch (error) {
                            setGlobalMessageFromException(error, false /* autoDismiss */);
                        }
                    }, BACKGROUND_DB_DATA_REFERSH_DELAY); // TODO: Should revisit as we are using default data.
                }
            }

            return dataFromDatabaseOrDefault;
        }
    }

    /**
    * Gets data from the network and updates the memory and database before returning the result
    * @param dataProvider Data provider
    */
    protected async loadDataFromNetwork<T>(dataProvider: IDataProvider<T>): Promise<T> {
        const dataFromNetwork: T = await dataProvider.getDataFromNetwork();
        return await this.updateDataInCache(dataFromNetwork, dataProvider);
    }

    /**
    * Stores the data to the network and updates the memory and database before returning the result
    * @param data Data to save
    * @param dataProvider Data provider
    */
    protected async updateData<T>(
            data: T,
            dataProvider: IDataProvider<T>
        ): Promise<T> {

        data = await dataProvider.saveDataToNetwork(data);

        return await this.updateDataInCache(data, dataProvider);
    }

    /**
     * Updates data in the memory store and the db
     * @param data data
     * @param dataProvider Data provider
     */
    protected async updateDataInCache<T>(
            data: T,
            dataProvider: IDataProvider<T>
        ): Promise<T> {

        // load the data into memory
        await dataProvider.setDataInMemory(data);

        // update the database, but don't await
        setTimeout(() => {
            dataProvider.setDataInDatabase(data);
        }, 0);

        return data;
    }

    /**
     * Deletes data in the memory store and the db
     * Note: This is for hard deleting data. Use updateDataInCache for data that is soft deleted.
     * @param data data
     * @param dataProvider Data provider
     */
    protected async hardDeleteDataInCache<T>(
            dataProvider: IDataProvider<T>
        ): Promise<void> {

        // update the database, but don't await
        setTimeout(() => { dataProvider.hardDeleteDataFromDatabase(); }, 0);

        await dataProvider.hardDeleteDataFromMemory();
    }

    public abstract resetSyncState(): Promise<void>;
}